import React, { FC, useContext } from 'react';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import ConnectToLabList from 'components/connect-to-lab/connect-to-lab-list.component';
import { useMutation, useQuery } from '@apollo/client';
import { connectedLabsListTemplate, disconnectSimpleClinicToLabTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LinearProgress } from '@mui/material';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

/** The clinic's connected labs, each with a disconnect button (DD-3696: the Labs page's "My labs" tab). */
const SimpleConnectedLabs: FC = () => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const {
        data: { labsForClinic } = {},
        loading,
        refetch,
    } = useQuery(connectedLabsListTemplate, {
        variables: { notConnected: false, searchText: '' },
        // DD-3696: a lab connected in the Find labs tab must show when this tab opens again.
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
    });

    const [disconnectSimpleClinicToLab] = useMutation(disconnectSimpleClinicToLabTemplate, {
        onCompleted: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('organization:LAB_DISCONNECTED'),
            });
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleDisconnectClick = (labId: string) => {
        disconnectSimpleClinicToLab({
            variables: {
                labId,
            },
        });
    };

    return (
        <Box>
            {loading ? (
                <LinearProgress />
            ) : (
                <ConnectToLabList
                    showConnected
                    labs={labsForClinic}
                    onConnectClick={() => refetch()}
                    onDisconnectClick={handleDisconnectClick}
                    onFetchMoreChange={() => {
                        return;
                    }}
                />
            )}
        </Box>
    );
};

export default SimpleConnectedLabs;
